// src/components/InstructorCard.js
import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Row, Col, Badge as ReactBootstrapBadge, Table } from 'react-bootstrap';
import { FaEdit, FaGlobe, FaChalkboardTeacher, FaTags, FaUserEdit, FaExternalLinkAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';

function InstructorCard({ author, onEdit, onDelete }) {
  const navigate = useNavigate();
  const instructor = author; // For better code readability since we're treating author data as instructor data
  const [courses, setCourses] = useState([]);
  const [showCoursesModal, setShowCoursesModal] = useState(false);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    // Don't fetch if the modal isn't shown to save resources
    if (showCoursesModal && instructor) {
      fetchInstructorCourses();
    }
  }, [showCoursesModal, instructor?.id]);

  const fetchInstructorCourses = async () => {
    if (!instructor) return;
    
    setLoading(true);
    try {
      const uid = localStorage.getItem("uid");
      if (!uid) {
        throw new Error("User not authenticated");
      }

      // Get all user courses
      const coursesQuery = query(
        collection(db, "books"), // Still using "books" collection for now
        where("userId", "==", uid)
      );
      
      const querySnapshot = await getDocs(coursesQuery);
      const instructorCourses = [];
      
      querySnapshot.forEach((doc) => {
        const courseData = doc.data();
        
        // Check if this course has this instructor (case insensitive)
        if (courseData.author && 
            courseData.author.toLowerCase().includes(instructor.name.toLowerCase())) {
          instructorCourses.push({
            id: doc.id,
            ...courseData
          });
        }
      });
      
      setCourses(instructorCourses);
    } catch (error) {
      console.error("Error fetching instructor courses:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle click on the main card
  const handleCardClick = () => {
    setShowCoursesModal(true);
  };

  // Format date to more readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <>
      <Card 
        className="instructor-card h-100" 
        onClick={handleCardClick}
        style={{ 
          borderRadius: '18px',
          maxWidth: '290px',
          margin: '0 auto',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          backgroundColor: '#fff',
          border: '1px solid #e0e0e0',
          cursor: 'pointer',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-5px)';
          e.currentTarget.style.boxShadow = '0 8px 15px rgba(0,0,0,0.1)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
        }}
      >
        {/* Instructor Image */}
        <div className="text-center pt-3 pb-2 px-2">
          {instructor?.imageUrl ? (
            <img 
              src={instructor.imageUrl} 
              alt={instructor.name || 'Instructor'} 
              className="instructor-image"
              style={{ 
                height: '180px',
                width: '180px',
                objectFit: 'cover',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                maxWidth: '95%',
                borderRadius: '50%'
              }}
            />
          ) : (
            <div 
              className="d-flex justify-content-center align-items-center bg-light" 
              style={{ 
                height: '180px',
                width: '180px',
                margin: '0 auto',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                maxWidth: '95%',
                borderRadius: '50%'
              }}
            >
              <span className="text-muted">No Image</span>
            </div>
          )}
        </div>

        {/* Instructor Name */}
        <Card.Body className="p-3 pt-2 text-center">
          <h5 className="mb-2 fw-bold" style={{ color: '#333' }}>
            {instructor?.name || 'Unknown Instructor'}
          </h5>
          
          {/* Country */}
          {instructor?.country && (
            <div className="mb-2 d-flex align-items-center justify-content-center">
              <FaGlobe className="me-1" size={14} style={{ color: '#5271ff' }} />
              <span style={{ color: '#666', fontSize: '0.9rem' }}>{instructor.country}</span>
            </div>
          )}
          
          {/* Tags/Subjects */}
          <div className="mt-3 d-flex flex-wrap justify-content-center gap-1">
            {instructor?.tags && instructor.tags.map((tag, index) => (
              <ReactBootstrapBadge 
                key={index} 
                bg="light" 
                text="dark" 
                style={{ 
                  fontSize: '0.7rem',
                  padding: '5px 8px',
                  borderRadius: '12px',
                  border: '1px solid #eee'
                }}
              >
                {tag}
              </ReactBootstrapBadge>
            ))}
          </div>
          
          {/* Course count */}
          <div className="mt-3 d-flex align-items-center justify-content-center">
            <FaChalkboardTeacher className="me-1" size={14} style={{ color: '#5271ff' }} />
            <span style={{ color: '#666', fontSize: '0.9rem' }}>
              {instructor?.bookCount || 0} {(instructor?.bookCount === 1) ? 'course' : 'courses'}
            </span>
          </div>
        </Card.Body>

        {/* Action Buttons */}
        <div className="mt-auto" style={{ borderTop: '1px solid #eee' }}>
          <div className="d-flex" onClick={(e) => e.stopPropagation()}>
            <Button 
              variant="link"
              className="flex-grow-1 m-0 py-2"
              style={{ 
                borderRadius: '0', 
                borderBottomLeftRadius: '18px',
                backgroundColor: 'transparent',
                borderColor: 'transparent',
                color: '#555',
                fontSize: '0.85rem',
                fontWeight: '400',
                boxShadow: 'none',
                textDecoration: 'none'
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (onEdit) onEdit(instructor);
              }}
            >
              <FaEdit className="me-1" /> Edit
            </Button>
            <Button 
              variant="link"
              className="flex-grow-1 m-0 py-2"
              style={{ 
                borderRadius: '0', 
                borderBottomRightRadius: '18px',
                backgroundColor: 'transparent',
                borderColor: 'transparent',
                color: '#555',
                fontSize: '0.85rem',
                fontWeight: '400',
                boxShadow: 'none',
                textDecoration: 'none'
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (onDelete) onDelete(instructor?.id);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      </Card>

      {/* Modal to show instructor's courses */}
      <Modal 
        show={showCoursesModal} 
        onHide={() => setShowCoursesModal(false)}
        size="lg"
        centered
        contentClassName="rounded-4 border-0 shadow-lg"
      >
        <Modal.Header 
          closeButton 
          className="border-0 pb-0 pt-4 px-4"
          style={{ backgroundColor: '#f8f9fa' }}
        >
          <Modal.Title className="fw-bold text-primary">
            Courses by {instructor?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading courses...</p>
            </div>
          ) : courses.length > 0 ? (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '60px' }}></th>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Added</th>
                    <th style={{ width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map((course) => (
                    <tr key={course.id} className="align-middle">
                      <td>
                        {course.imageUrl ? (
                          <img 
                            src={course.imageUrl} 
                            alt={course.title} 
                            style={{ 
                              width: '45px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '4px'
                            }}
                          />
                        ) : (
                          <div 
                            className="bg-light d-flex align-items-center justify-content-center" 
                            style={{ 
                              width: '45px',
                              height: '60px',
                              borderRadius: '4px'
                            }}
                          >
                            <span className="text-muted small">No Cover</span>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="fw-semibold">{course.title}</div>
                        <div className="text-muted small">
                          {course.tags && course.tags.length > 0 && course.tags[0]}
                        </div>
                      </td>
                      <td>
                        {course.readingStatus === 'completed' ? (
                          <span className="badge bg-success bg-opacity-10 text-success">Completed</span>
                        ) : course.readingStatus === 'inprogress' ? (
                          <span className="badge bg-primary bg-opacity-10 text-primary">In Progress</span>
                        ) : course.readingStatus ? (
                          <span className="badge bg-secondary bg-opacity-10 text-secondary">Upcoming</span>
                        ) : course.inWishlist ? (
                          <span className="badge bg-info bg-opacity-10 text-info">Wishlist</span>
                        ) : (
                          <span className="badge bg-light text-dark">No Status</span>
                        )}
                      </td>
                      <td>{formatDate(course.dateAdded)}</td>
                      <td>
                        <Button 
                          size="sm" 
                          variant="outline-primary" 
                          className="rounded-2"
                          onClick={() => navigate(`/course/${course.id}/edit`)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-5 my-3">
              <div 
                className="bg-light rounded-4 py-5 mx-auto"
                style={{ maxWidth: '450px' }}
              >
                <p className="text-muted mb-0">No courses found for this instructor in your library.</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 px-4">
          <Button 
            variant="secondary" 
            onClick={() => setShowCoursesModal(false)}
            className="rounded-pill px-4"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default InstructorCard;